import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../services/firebase';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  multiFactor,
  TotpMultiFactorGenerator,
  getMultiFactorResolver
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import persistentStorage from '../services/PersistentStorageService';

const AuthContext = createContext({});

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirectPath, setRedirectPath] = useState(null);

  // MFA state
  const [mfaResolver, setMfaResolver] = useState(null);

  // Prevent duplicate profile fetches
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileFetched, setProfileFetched] = useState(false);

  // Check and restore persistent data for user
  const checkAndRestorePersistentData = async (uid) => {
    try {
      console.log('🔍 Checking broker connections for user:', uid);
      
      // ตรวจสอบครั้งแรก
      let hasActiveConnections = await checkBrokerConnections(uid);
      
      // ถ้าไม่มี connection ให้รอและตรวจสอบอีกครั้ง
      if (!hasActiveConnections) {
        console.log('⏳ No connections found, waiting for real-time update...');
        
        // รอ 3 วินาที แล้วตรวจสอบอีกครั้ง
        await new Promise(resolve => setTimeout(resolve, 3000));
        hasActiveConnections = await checkBrokerConnections(uid);
        
        if (hasActiveConnections) {
          console.log('✅ Found connections after retry');
        }
      }
      
      console.log('🔍 Has active broker connections:', hasActiveConnections);
      
      // Broker connection redirect logic is now handled by BrokerConnectionGuard
      // No need to set redirect path here anymore
      
    } catch (error) {
      console.error('❌ Error checking broker connections:', error);
      // Stay on dashboard on error
    }
  };

  // Helper function to check broker connections
  const checkBrokerConnections = async (uid) => {
    const brokerQuery = query(
      collection(db, 'tbl_broker_integration'),
      where('userId', '==', uid)
    );
    const brokerSnapshot = await getDocs(brokerQuery);
    
    let hasActiveConnections = false;
    brokerSnapshot.forEach((doc) => {
      const integration = doc.data();
      console.log('🔍 Broker:', integration.broker, 'Status:', integration.status);
      if (integration.status === 'active' || integration.status === 'connected') {
        hasActiveConnections = true;
      }
    });
    
    return hasActiveConnections;
  };

  // Fetch user profile from Firestore
  const fetchUserProfile = async (uid) => {
    // Prevent duplicate fetches
    if (isProfileLoading || (profileFetched && userProfile && userProfile.uid === uid)) {
      console.log('🔍 AuthContext: Profile already loading or fetched, skipping');
      return;
    }

    setIsProfileLoading(true);
    try {
      console.log('🔍 AuthContext: Fetching profile from Firestore for:', uid);
      const userDoc = await getDoc(doc(db, 'users', uid));
      
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        console.log('✅ AuthContext: Profile found:', {
          username: profileData.username,
          email: profileData.email,
          hasData: !!profileData
        });
        setUserProfile(profileData);
        setProfileFetched(true);
      } else {
        // Create a minimal profile if it doesn't exist
        console.warn('⚠️ AuthContext: User profile not found, creating minimal profile');
        const minimalProfile = {
          uid: uid,
          email: auth.currentUser?.email || '',
          username: auth.currentUser?.email?.split('@')[0] || `user_${uid.substring(0, 8)}`,
          createdAt: new Date().toISOString()
        };
        console.log('📝 AuthContext: Created minimal profile:', minimalProfile);
        setUserProfile(minimalProfile);
        setProfileFetched(true);
      }
    } catch (error) {
      console.error('❌ AuthContext: Error fetching user profile:', error);
      // Still set a minimal profile to prevent blocking
      const fallbackProfile = {
        uid: uid,
        email: auth.currentUser?.email || '',
        username: `user_${uid.substring(0, 8)}`,
        error: true
      };
      console.log('🔄 AuthContext: Using fallback profile:', fallbackProfile);
      setUserProfile(fallbackProfile);
      setProfileFetched(true);
      // Don't set error state - just log it
      console.warn('Using fallback profile due to error');
    } finally {
      setIsProfileLoading(false);
    }
  };

  // Monitor auth state changes
  useEffect(() => {
    console.log('🔍 AuthContext: Setting up auth state listener');
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔍 AuthContext: Auth state changed:', {
        hasUser: !!user,
        uid: user?.uid,
        email: user?.email,
        currentPath: window.location.pathname,
        timestamp: new Date().toISOString()
      });
      
      setUser(user);
      if (user) {
        // Clear any pending MFA challenge now that auth fully succeeded
        setMfaResolver(null);
        console.log('🔍 AuthContext: User found, fetching profile for:', user.uid);
        await fetchUserProfile(user.uid);
        
        // Set default redirect path first
        setRedirectPath('/dashboard');
        
        // Check broker connections in background (for logging purposes)
        checkAndRestorePersistentData(user.uid).catch(error => {
          console.error('❌ AuthContext: Error in persistent data check:', error);
        });
        
        // Debug: Store Firebase Token for development testing only
        if (process.env.NODE_ENV === 'development') {
          try {
            const token = await user.getIdToken(true);
            window.firebaseToken = token;
          } catch (error) {
            console.error("Error getting Firebase token:", error);
          }
        }
      } else {
        console.log('🔍 AuthContext: No user, clearing profile');
        setUserProfile(null);
        setProfileFetched(false);
        setIsProfileLoading(false);
        setMfaResolver(null);
      }
      setLoading(false);
    });
    
    // Cleanup function
    return () => {
      unsubscribe();
    };
  }, []);

  // Broker connection events are now handled by BrokerConnectionGuard
  // No need for event listeners here anymore

  // Sign up with additional user data
  const signUp = async (email, password, additionalData) => {
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create user profile in Firestore
      // qfaa-cc#42: write tier='tier1' as the canonical top-level field so
      // orderController's normalizeTier never defaults silently. The
      // subscription webhook will overwrite this when the purchase lands.
      const userProfile = {
        uid: user.uid,
        email: email,
        fullName: additionalData.fullName,
        phoneNumber: additionalData.phoneNumber,
        username: additionalData.username,
        tier: 'tier1',
        timezone: 'Eastern Time (ET)',
        status: 'active',
        notificationPreferences: {
          emailNotifications: true
        },
        subscription: {
          plan: 'free', // free, basic, premium, enterprise
          status: 'active', // active, inactive, cancelled
          startDate: serverTimestamp(),
          endDate: null,
          features: []
        },
        lastLogin: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(doc(db, 'users', user.uid), userProfile);
      setUserProfile(userProfile);
      
      return user;
    } catch (error) {
      // Customize error messages for better UX
      let customMessage = error.message;
      
      if (error.code === 'auth/email-already-in-use') {
        customMessage = 'Email already in use';
      } else if (error.code === 'auth/user-not-found') {
        customMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        customMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        customMessage = 'Invalid email format';
      } else if (error.code === 'auth/weak-password') {
        customMessage = 'Password is too weak';
      } else if (error.code === 'auth/admin-restricted-operation') {
        customMessage =
          'Account creation is restricted. Use the create-account link from your purchase email, or contact support.';
      }
      
      setError(customMessage);
      throw error;
    }
  };

  // Helper function to get email from username
  const getEmailFromUsername = async (username) => {
    try {
      console.log('🔍 Looking up username:', username);
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Username lookup timeout')), 30000); // 30 seconds
      });
      
      const lookupPromise = (async () => {
        const q = query(collection(db, 'users'), where('username', '==', username));
        const querySnapshot = await getDocs(q);
        
        console.log('📊 Username lookup result:', {
          username,
          found: !querySnapshot.empty,
          count: querySnapshot.size
        });
        
        if (querySnapshot.empty) {
          console.log('❌ Username not found in Firestore:', username);
          return null;
        }
        
        // Return the email of the first matching user
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        console.log('✅ Username found:', {
          username,
          email: userData.email,
          uid: userDoc.id
        });
        
        return userData.email;
      })();
      
      const email = await Promise.race([lookupPromise, timeoutPromise]);
      return email;
    } catch (error) {
      console.error('❌ Error looking up username:', error);
      if (error.message === 'Username lookup timeout') {
        throw new Error('Login timeout. Please try again.');
      }
      return null;
    }
  };

  // Sign in with email or username
  const signIn = async (emailOrUsername, password, notificationHelper = null) => {
    setError(null);
    try {
      let email = emailOrUsername;
      let isUsernameLogin = false;
      
      // Check if input is an email (contains @) or username
      if (!emailOrUsername.includes('@')) {
        // It's a username, look up the email
        isUsernameLogin = true;
        const foundEmail = await getEmailFromUsername(emailOrUsername);
        if (!foundEmail) {
          throw new Error('Username not found');
        }
        email = foundEmail;
      }
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user document exists
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        // Create user document with default values if it doesn't exist
        const defaultUserProfile = {
          uid: user.uid,
          email: user.email,
          fullName: user.displayName || '',
          phoneNumber: user.phoneNumber || '',
          username: user.email.split('@')[0], // Use email prefix as default username
          timezone: 'Eastern Time (ET)',
          status: 'active',
          notificationPreferences: {
            emailNotifications: true
          },
          subscription: {
            plan: 'free',
            status: 'active',
            startDate: serverTimestamp(),
            endDate: null,
            features: []
          },
          lastLogin: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };
        
        await setDoc(userRef, defaultUserProfile);
        console.log('Created missing user document for:', user.email);
      } else {
        // Update lastLogin timestamp for existing user
        const updateData = {
          lastLogin: serverTimestamp()
        };
        
        // If this was a username login and the user document doesn't have a username,
        // add the username to the document
        if (isUsernameLogin) {
          const userData = userDoc.data();
          if (!userData.username) {
            updateData.username = emailOrUsername; // Use the username that was used to login
            console.log('Added missing username to user document:', emailOrUsername);
          }
        }
        
        await updateDoc(userRef, updateData);
      }
      
      // Fetch updated user profile first
      await fetchUserProfile(user.uid);
      
      // Set redirect path; actual navigation handled by ProtectedRoute to avoid flicker
      console.log('🎯 Setting redirect path to /dashboard');
      setRedirectPath('/dashboard');
      
      // Send login notification if helper is provided
      if (notificationHelper) {
        try {
          await notificationHelper();
        } catch (notifError) {
          console.error('Failed to send notification:', notifError);
        }
      }
      
      console.log('✅ Login process completed successfully');
      return user;
    } catch (error) {
      // Handle MFA challenge
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        // Re-throw with a specific code so LoginPage can redirect
        throw error;
      }

      // Customize error messages for better UX
      let customMessage = error.message;

      if (error.code === 'auth/user-not-found') {
        customMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        customMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        customMessage = 'Invalid email format';
      } else if (error.code === 'auth/invalid-credential') {
        customMessage = 'Invalid credentials';
      }

      setError(customMessage);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    setError(null);
    try {
      // Store current page before logout
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/signup') {
        persistentStorage.storeLastVisitedPage(currentPath);
        console.log('💾 Stored last visited page before logout:', currentPath);
      }
      
      // Store current dashboard state if on dashboard
      if (currentPath === '/dashboard') {
        // Store dashboard state for restoration
        const dashboardState = {
          stocks: [], // This will be populated by the dashboard component
          portfolioValue: 0,
          selectedTab: 'overview',
          filters: {},
          sortBy: 'symbol',
          sortOrder: 'asc',
          lastUpdated: new Date().toISOString()
        };
        persistentStorage.storeDashboardState(dashboardState);
        console.log('💾 Stored dashboard state before logout');
      }
      
      // Store user preferences
      const userPreferences = {
        theme: 'dark',
        language: 'en',
        notifications: true,
        autoRefresh: true,
        lastVisitedPage: currentPath
      };
      persistentStorage.storeUserPreferences(userPreferences);
      console.log('💾 Stored user preferences before logout');
      
      await signOut(auth);
      setUserProfile(null);
      setRedirectPath(null);
      setMfaResolver(null);
      
      console.log('✅ Logout successful, all persistent data stored');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!user) return;
    
    setError(null);
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setUserProfile(prev => ({
        ...prev,
        ...updates
      }));
      
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Update password (re-authenticates first, then updates and logs to Firestore)
  const updateUserPassword = async (currentPassword, newPassword) => {
    if (!user) throw new Error('Not authenticated');

    setError(null);
    try {
      // Re-authenticate before sensitive operation
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password in Firebase Auth
      await updatePassword(user, newPassword);

      // Log password change to Firestore
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          passwordUpdatedAt: serverTimestamp(),
          lastPasswordChange: serverTimestamp()
        });
      } catch (firestoreError) {
        // Password was updated successfully, Firestore log is non-critical
        console.warn('Failed to log password change to Firestore:', firestoreError);
      }

      return true;
    } catch (error) {
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Clear redirect path
  const clearRedirectPath = () => {
    setRedirectPath(null);
    console.log('🗑️ Cleared redirect path');
  };

  // ── MFA helpers ────────────────────────────────────────────────────────

  /** Complete MFA sign-in with a TOTP code */
  const verifyMfaCode = async (otpCode) => {
    if (!mfaResolver) throw new Error('No MFA challenge pending');

    try {
      const totpHint = mfaResolver.hints.find(
        (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      if (!totpHint) throw new Error('No TOTP factor enrolled');

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        otpCode
      );
      const userCredential = await mfaResolver.resolveSignIn(assertion);
      setMfaResolver(null);
      return userCredential.user;
    } catch (error) {
      // Clear resolver on unrecoverable session errors so user is forced to re-login
      if (
        error.code === 'auth/session-expired' ||
        error.code === 'auth/invalid-multi-factor-session' ||
        error.code === 'auth/totp-challenge-timeout'
      ) {
        setMfaResolver(null);
      }
      throw error;
    }
  };

  /** Clear pending MFA state (e.g. user cancels) */
  const clearMfaResolver = () => {
    setMfaResolver(null);
  };

  /** Start TOTP enrollment — returns { secret, totpUri, qrUri } */
  const startTotpEnrollment = async () => {
    if (!user) throw new Error('Must be signed in');
    const mfaSession = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(mfaSession);
    const totpUri = secret.generateQrCodeUrl(user.email, 'QFAA');
    return { secret, totpUri };
  };

  /** Finalise TOTP enrollment with the verification code */
  const finalizeTotpEnrollment = async (secret, verificationCode) => {
    if (!user) throw new Error('Must be signed in');
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      secret,
      verificationCode
    );
    await multiFactor(user).enroll(assertion, 'Authenticator App');
  };

  /** Unenroll TOTP (requires the enrolled factor info) */
  const unenrollTotp = async () => {
    if (!user) throw new Error('Must be signed in');
    const enrolledFactors = multiFactor(user).enrolledFactors;
    const totpFactor = enrolledFactors.find(
      (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
    );
    if (!totpFactor) throw new Error('TOTP not enrolled');
    await multiFactor(user).unenroll(totpFactor);
  };

  /** Check if current user has TOTP enrolled */
  const isTotpEnrolled = () => {
    if (!user) return false;
    const enrolledFactors = multiFactor(user).enrolledFactors;
    return enrolledFactors.some(
      (f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
    );
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    redirectPath,
    clearRedirectPath,
    signUp,
    signIn,
    logout,
    updateUserProfile,
    updateUserPassword,
    resetPassword,
    // MFA
    mfaResolver,
    verifyMfaCode,
    clearMfaResolver,
    startTotpEnrollment,
    finalizeTotpEnrollment,
    unenrollTotp,
    isTotpEnrolled
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 